// src/components/Ielts_test/hasil/reading/ResultModal.jsx
import { CircleCheck, CircleX } from "lucide-react";
import { useEffect, useState } from "react";

export default function ResultModal({
  result,
  data,
  duration,
  onClose,
  test_type,
}) {
  const [bandScore, setBandScore] = useState(0);

  const resultByPassage = data.parts.map((part, pIndex) => {
    const questions = part.sections.flatMap((s) => s.questions);

    const answers = questions.map(
      (q) =>
        result.detail_answers.find((a) => Number(a.question_id) === q.id) || {
          question_id: q.id,
          number: q.number,
          skipped: true,
          is_correct: false,
        },
    );

    return {
      ...part,
      answers,
    };
  });

  // Konversi skor ke band (simulasi IELTS)
  useEffect(() => {
    const percent = result.score_percent;
    let band = 0;
    if (percent >= 90) band = 9.0;
    else if (percent >= 80) band = 8.0;
    else if (percent >= 70) band = 7.0;
    else if (percent >= 60) band = 6.5;
    else if (percent >= 50) band = 6.0;
    else if (percent >= 40) band = 5.5;
    else if (percent >= 30) band = 5.0;
    else if (percent >= 20) band = 4.0;
    else band = 3.0;
    setBandScore(band);
  }, [result]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const totalQuestions = resultByPassage.reduce(
    (sum, part) => sum + part.answers.length,
    0,
  );

  const correctCount = resultByPassage.reduce(
    (sum, part) =>
      sum + part.answers.filter((a) => a.is_correct === "1").length,
    0,
  );

  return (
    <div className="fixed inset-0 bg-white flex flex-col items-center overflow-y-auto z-50 p-4 md:p-8">
      {/* Header */}
      <div className="w-full max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
              IELTS
            </div>
            <div>
              <h1 className="font-anton text-2xl text-primary">
                IELTS {test_type} Test
              </h1>
              <p className="text-sm text-gray-600">Academic • 60 minutes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Time & Score */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
          <p className="text-sm text-gray-600">username</p>
          <h2 className="font-anton text-xl text-primary mt-1">
            Your score is:
          </h2>
          <div className="flex justify-center items-center gap-8 mt-4">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-4 border-gray-300 flex items-center justify-center text-3xl font-bold text-gray-700">
                {formatTime(duration)}
              </div>
              <p className="text-xs text-gray-600 mt-2">Time Taken</p>
            </div>
            <div className="text-center">
              <div className="w-28 h-28 rounded-full border-8 border-primary flex items-center justify-center text-5xl font-bold text-primary">
                {bandScore}
              </div>
              <p className="text-sm font-poppins mt-2">Band Score</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-4 border-gray-300 flex items-center justify-center text-3xl font-bold text-gray-700">
                {correctCount}/{totalQuestions}
              </div>
              <p className="text-xs text-gray-600 mt-2">Correct</p>
            </div>
          </div>
        </div>

        {/* Band Score Chart */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-8">
          <p className="text-center text-sm font-poppins text-gray-600 mb-3">
            Band Score:
          </p>
          <div className="flex justify-center gap-1">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <div
                key={i}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                  ${
                    i === Math.floor(bandScore)
                      ? "bg-primary text-white"
                      : "bg-gray-200 text-gray-600"
                  }
                  ${
                    i === Math.ceil(bandScore) && bandScore % 1 !== 0
                      ? "bg-primary text-white"
                      : ""
                  }
                `}
              >
                {i}
              </div>
            ))}
          </div>
        </div>

        {/* Answer Keys */}
        <div className="space-y-6">
          {resultByPassage.map((part, pIndex) => {
            const startNumber = part.answers[0]?.number;
            const endNumber = part.answers[part.answers.length - 1]?.number;
            return (
              <div
                key={`${part.passage_id}-${pIndex}`}
                className="bg-white rounded-xl shadow-md p-5"
              >
                <h3 className="font-anton text-lg text-primary mb-4">
                  Part {pIndex + 1}: Questions {startNumber}–{endNumber}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {part.answers.map((ans) => {
                    return (
                      <div
                        key={ans.question_id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="font-poppins font-semibold text-gray-700 w-6">
                          {ans.number}.
                        </span>
                        <span className="flex-1">
                          {ans.skipped
                            ? "-"
                            : (ans.answare_text ?? ans.selected_option_id)}
                        </span>
                        {ans.is_correct === "1" ? (
                          <CircleCheck className="text-green-600" />
                        ) : (
                          <CircleX className="text-red-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
